import React from "react";
import { FiCloud, FiExternalLink } from "react-icons/fi";
import { useAnalyticsOverview, type InstanceSummary } from "../../hooks/useAnalytics";
import { PanelCard, TrendChart, type TrendPoint } from "./dashboardShared";
import { appendPreviewAccessCode } from "../../Helper/siteLinks";

const compact = new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 });
const full = new Intl.NumberFormat("fr-FR");
const ratioFmt = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const dayFmt = new Intl.DateTimeFormat("fr-FR", { day: "numeric" });
const weekdayFmt = new Intl.DateTimeFormat("fr-FR", { weekday: "short" });
const fullDateFmt = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" });

/** Parse an ISO yyyy-MM-dd as a LOCAL date so the rendered day never shifts by a timezone offset. */
const parseLocalDay = (iso: string): Date => {
	const [y, m, d] = (iso || "").split("-").map(Number);
	return new Date(y || 1970, (m || 1) - 1, d || 1);
};

/** Daily visits → chart points, labelling each day (weekday + day-of-month). For windows longer
 * than ~8 days only every n-th day is labelled to avoid crowding, but every day keeps its dot + hover. */
const buildTrend = (series: InstanceSummary["series"]): TrendPoint[] => {
	const points = series ?? [];
	const stride = points.length > 8 ? Math.ceil(points.length / 7) : 1;
	return points.map((p, i) => {
		const dt = parseLocalDay(p.date);
		const labelled = i % stride === 0 || i === points.length - 1;
		return {
			value: p.visits,
			label: labelled ? dayFmt.format(dt) : undefined,
			sublabel: labelled ? weekdayFmt.format(dt).replace(".", "") : undefined,
			title: `${fullDateFmt.format(dt)} · ${full.format(p.visits)} visite${p.visits > 1 ? "s" : ""}`,
		};
	});
};

const TARGET_LABEL: Record<string, string> = { production: "Production", preview: "Preview" };

/**
 * One public instance: VISITES (sessions) as the hero, PAGES/VISITE (engagement : page views ÷ visits)
 * as the companion so the second number is NOT a near-duplicate of the first, plus the total page-view
 * count as a caption. The chart below plots daily visits with a labelled day axis + per-day hover value.
 */
const InstanceCard: React.FC<{ instance: InstanceSummary }> = ({ instance }) => {
	const isProd = instance.target === "production";
	const perVisit = instance.visits > 0 ? instance.pageViews / instance.visits : 0;
	const series = instance.series ?? [];
	const trend = buildTrend(series);
	// L'hôte affiché devient un vrai lien vers le site ; la preview porte le code d'accès
	// hebdomadaire (?ac=), publié app-wide par usePreviewAccessCode, pour ne jamais buter
	// sur le gate en cliquant depuis l'admin.
	const siteUrl = instance.host ? (isProd ? `https://${instance.host}` : appendPreviewAccessCode(`https://${instance.host}`)) : null;

	return (
		<PanelCard
			title={TARGET_LABEL[instance.target] ?? instance.target}
			subtitle={
				siteUrl ? (
					<a
						href={siteUrl}
						target="_blank"
						rel="noreferrer"
						className="inline-flex max-w-full items-center gap-1 truncate align-bottom hover:text-sky-600 hover:underline"
					>
						{instance.host}
						<FiExternalLink className="h-3 w-3 shrink-0" aria-hidden />
					</a>
				) : (
					instance.host
				)
			}
			bodyClassName="p-5"
			action={
				<span
					className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
						isProd ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
					}`}
				>
					<span className={`h-1.5 w-1.5 rounded-full ${isProd ? "bg-emerald-500" : "bg-amber-500"}`} />
					{isProd ? "Site public" : "Preview"}
				</span>
			}
		>
			{!instance.configured ? (
				<p className="text-xs text-slate-400">{instance.reason || "Trafic non disponible."}</p>
			) : (
				<>
					<div className="flex items-start justify-between gap-4">
						<div>
							<p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Visites</p>
							<p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900" title={full.format(instance.visits)}>
								{compact.format(instance.visits)}
							</p>
							<p className="mt-1 text-[11px] text-slate-400">{full.format(instance.pageViews)} pages vues · sans robots</p>
						</div>
						<div className="text-right">
							<p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Pages / visite</p>
							<p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900">{ratioFmt.format(perVisit)}</p>
							<p className="mt-1 text-[11px] text-slate-400">profondeur de visite</p>
						</div>
					</div>
					<div className="mt-4">
						{series.length >= 2 ? (
							<TrendChart points={trend} color={isProd ? "#0095DB" : "#F59E0B"} height={56} />
						) : (
							<p className="py-6 text-center text-xs text-slate-400">L'historique se remplit (Web Analytics activé récemment).</p>
						)}
					</div>
				</>
			)}
		</PanelCard>
	);
};

/** The calm "connect Cloudflare" state shown until a Web-Analytics account + token are configured. */
const ConnectCard: React.FC<{ reason?: string | null }> = ({ reason }) => (
	<PanelCard title="Audience des sites" subtitle="Cloudflare Web Analytics" bodyClassName="p-5">
		<div className="flex min-h-[180px] flex-col items-center justify-center gap-2 text-center">
			<span className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-50 text-sky-500">
				<FiCloud className="h-5 w-5" />
			</span>
			<p className="text-sm font-semibold text-slate-700">Connecter Cloudflare Web Analytics</p>
			<p className="max-w-md text-xs text-slate-500">
				{reason || "Web Analytics non configuré."} Renseignez{" "}
				<code className="rounded bg-slate-100 px-1">CloudflareAnalytics:AccountId</code> et un token avec la portée Account Analytics:Read.
			</p>
			<a
				href="https://dash.cloudflare.com/?to=/:account/web-analytics"
				target="_blank"
				rel="noreferrer"
				className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700 hover:underline"
			>
				Ouvrir Cloudflare Web Analytics <FiExternalLink className="h-3 w-3" />
			</a>
		</div>
	</PanelCard>
);

const LoadingCard: React.FC = () => (
	<PanelCard title="Audience des sites" subtitle="Cloudflare Web Analytics" bodyClassName="p-5">
		<div className="grid gap-4 sm:grid-cols-2">
			<div className="h-28 animate-pulse rounded-lg bg-slate-100" />
			<div className="h-28 animate-pulse rounded-lg bg-slate-100" />
		</div>
		<div className="mt-4 h-40 w-full animate-pulse rounded-lg bg-slate-100" />
	</PanelCard>
);

/**
 * Dashboard sites-audience row: the public sites' REAL audience from Cloudflare Web Analytics (RUM) : * cookieless, bot-filtered browser traffic filtered to the BCC hostnames. The two per-instance cards
 * (Production + Preview) span the full width. Each card shows VISITES (sessions) + PAGES/VISITE
 * (engagement) + total page views. Read-only; degrades to a calm "connect Cloudflare" / loading card
 * when no account/token is configured.
 */
export const AnalyticsSection: React.FC = () => {
	const { data, loading } = useAnalyticsOverview(7);

	if (loading) return <LoadingCard />;
	if (!data || !data.configured) return <ConnectCard reason={data?.reason} />;

	const instances = data.instances ?? [];
	return (
		// 2-up from tablet (md) so Production & Preview sit side by side with their headers/figures aligned.
		<div className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-2">
			{instances.map((instance) => (
				<InstanceCard key={instance.target} instance={instance} />
			))}
		</div>
	);
};

export default AnalyticsSection;
